import type { EntityItemsGroupRow, EntityItemsRequestV2 } from '@jms/model';
import type { IRowNode } from 'ag-grid-enterprise';

/** Leaves per SSRM block: a plan opens on its first 100 visits and scrolling reads the next. */
export const SERVER_GROUP_BLOCK_SIZE = 100;

/** Marks a synthetic group row, so the leaf row id resolver never sees it. */
const SERVER_GROUP_ROW = '__serverGroupRow';

type ServerGroupRowData = Record<string, unknown> & { [SERVER_GROUP_ROW]: EntityItemsGroupRow };

/**
 * One group row as SSRM takes it. The group field holds `{ _id: groupKey }` because the table's
 * lookup keyCreator reads `_id`: that key is what SSRM hands back as `groupKeys` when the group opens.
 */
export function serverGroupRowData(field: string, row: EntityItemsGroupRow): ServerGroupRowData {

  const data: ServerGroupRowData = { [SERVER_GROUP_ROW]: row };
  const parts = field.split('.');
  let cursor: Record<string, unknown> = data;
  for (const part of parts.slice(0, -1)) {
    const next: Record<string, unknown> = {};
    cursor[part] = next;
    cursor = next;
  }
  cursor[parts[parts.length - 1]] = { _id: row.groupKey };
  return data;
}

/** The group row a server-grouped node carries; null for a leaf or any other board's group. */
export function readServerGroupRow<G = unknown>(node: Pick<IRowNode, 'data'> | null | undefined): EntityItemsGroupRow<G> | null {

  return (node?.data?.[SERVER_GROUP_ROW] as EntityItemsGroupRow<G> | undefined) ?? null;
}

/** Group ids stay apart from leaf ids, which the board resolves from the record itself. */
export function serverGroupRowId(data: unknown): string | null {

  const row = readServerGroupRow({ data });
  return row ? `server-group:${row.groupKey}` : null;
}

/** The board's own request narrowed to one group's block of leaves; counted, because the grid sizes its scrollbar from it. */
/** SSRM hands back the group column's raw value (`{ _id }`) when no keyCreator runs on the server model; the request needs the id. */
export function serverGroupKeyOf(value: unknown): string {

  if (value && typeof value === 'object' && '_id' in value) return String((value as { _id: unknown })._id);
  return String(value);
}

export function serverGroupLeafRequest(request: EntityItemsRequestV2, groupKey: string, startRow: number, pageSize = SERVER_GROUP_BLOCK_SIZE): EntityItemsRequestV2 {

  return {
    ...request,
    groupKey,
    page: Math.floor(startRow / pageSize),
    pageSize,
    unpaged: false,
    skipCount: false,
  };
}
